import { makeAutoObservable } from 'mobx'
import type { IIconProps } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type ISmartCallFlowPlayAudioStep } from 'entities/SmartCall/api/types'
import type { Inbox } from 'entities/Inbox/model/Inbox'
import { FlowActionStore } from 'widgets/Flow'
import {
  generateSmartCallActionId,
  type ISmartCallActionStore,
  type ISmartCallFlowActionStoreProps,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/Action'
import { GreetingAudioBlockStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/blocks/GreetingAudioBlock'
import { type ISmartCallFlowActionType } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'

export type IPlayAudioStoreProps = {
  id?: ISmartCallFlowActionStoreProps['id']
  parentId: ISmartCallFlowActionStoreProps['parentId']
  args?: ISmartCallFlowPlayAudioStep['args']
}

export class PlayAudioStore {
  static readonly type = 'playAudio' satisfies ISmartCallFlowActionType
  static title = 'Play audio'
  static iconProps: IIconProps = {
    icon: 'voice',
    color: 'var(--action-contained-primary-normal)',
  }
  public readonly actionStore: ISmartCallActionStore

  readonly greetingAudioBlockStore: GreetingAudioBlockStore

  constructor(
    private _entities: IEntitiesStore,
    { id, args, ...props }: IPlayAudioStoreProps,
    private parent: { inbox: Inbox | null }
  ) {
    this.actionStore = new FlowActionStore({
      ...props,
      id: id || generateSmartCallActionId('action_playAudio'),
      type: PlayAudioStore.type,
      name: PlayAudioStore.title,
      allowedNextActions: [
        'ring',
        'ivr',
        'forwardCall',
        'sendSMS',
        'aiTextingAgent',
        'aiCallingAgent',
      ],
    })
    this.greetingAudioBlockStore = new GreetingAudioBlockStore(
      this._entities,
      this.parent,
      'Add audio message to continue',
      args
    )
    makeAutoObservable(this)
  }

  get step(): ISmartCallFlowPlayAudioStep {
    return {
      id: 'playAudio',
      type: 'action',
      args: this.greetingAudioBlockStore.args,
      next: this.actionStore.nextId,
    }
  }

  setApiError = (key: string, message: string) => {
    if (key.includes('audioUrl')) {
      this.greetingAudioBlockStore.setError(message)
    }
  }

  validate = () => {
    return this.greetingAudioBlockStore.validate()
  }

  get isConfigurationError() {
    return !!this.greetingAudioBlockStore.error
  }
}
